import { type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

interface HelpCrunchContextValue {
    isReady: boolean;
}

const HelpCrunchContext = createContext<HelpCrunchContextValue>({
    isReady: false,
});

interface HelpCrunchProviderProps {
    children: ReactNode;
    organization: string;
    appId: string;
}

declare global {
    interface Window {
        helpcrunchSettings: {
            organization: string;
            appId: string;
        };
        HelpCrunch: {
            (...args: unknown[]): void;
            q?: unknown[];
        };
        helpcrunchLastUserData?: string | null;
    }
}

export function HelpCrunchProvider({
    children,
    organization,
    appId,
}: HelpCrunchProviderProps) {
    const { helpCrunchUserData } = usePage<SharedData>().props;

    // Check if HelpCrunch is already loaded during initial state setup
    const widgetSrc = `https://${organization}.widget.helpcrunch.com/`;
    const isAlreadyLoaded =
        typeof window.HelpCrunch === 'function' &&
        document.querySelector(`script[src="${widgetSrc}"]`) !== null;
    const [isReady, setIsReady] = useState(isAlreadyLoaded);
    const lastUserData = useRef<string | null>(null);

    // Initialize lastUserData from window if it exists (persists across Inertia navigations)
    useEffect(() => {
        if (window.helpcrunchLastUserData !== undefined) {
            lastUserData.current = window.helpcrunchLastUserData;
        }
    }, []);

    useEffect(() => {
        // Skip loading if appId or organization are not present
        if (!appId || !organization) {
            return;
        }

        // Set up HelpCrunch settings
        window.helpcrunchSettings = {
            organization,
            appId,
        };

        if (isReady) {
            return;
        }

        // Create HelpCrunch function stub if it doesn't exist
        if (!window.HelpCrunch) {
            window.HelpCrunch = Object.assign(
                function (...args: unknown[]) {
                    window.HelpCrunch.q = window.HelpCrunch.q || [];
                    window.HelpCrunch.q.push(args);
                },
                { q: [] as unknown[] },
            );
        }

        // Listen for onReady event
        window.HelpCrunch('onReady', () => {
            setIsReady(true);
        });

        // Load the script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = widgetSrc;
        (document.body || document.head).appendChild(script);
    }, [organization, appId, widgetSrc, isReady]);

    const updateUserInHelpCrunch = (
        helpCrunchUserData: SharedData['helpCrunchUserData'],
    ) => {
        const currentUserData = helpCrunchUserData
            ? JSON.stringify(helpCrunchUserData)
            : null;

        // Only call updateUser if user data has actually changed
        if (currentUserData !== lastUserData.current) {
            lastUserData.current = currentUserData;
            window.helpcrunchLastUserData = currentUserData;

            if (helpCrunchUserData) {
                window.HelpCrunch('updateUser', helpCrunchUserData);
            }
        }
    };

    useEffect(() => {
        if (!isReady) return;

        updateUserInHelpCrunch(helpCrunchUserData);
    }, [helpCrunchUserData, isReady]);

    return (
        <HelpCrunchContext.Provider value={{ isReady }}>
            {children}
        </HelpCrunchContext.Provider>
    );
}

export function useHelpCrunch() {
    return useContext(HelpCrunchContext);
}
